<template>
  <ModalDialog
    ref="dialog"
    title="Cancel Order Item"
    :submitting="submitting"
    submitTitle="Cancel Item"
    icon="fa-times-circle"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3">
      <NotificationBar type="warning">
        <NotificationBarTitle>This will cancel the item and it will not be restoreable</NotificationBarTitle>
        <ul>
          <li>The item will stop all production and despatch tasks associated with the item</li>
          <li>If set, item fees will be adjusted based on stages reached in production</li>
          <li>If set, additional fees below will be applied to cover order entry</li>
        </ul>
        <strong>Note:</strong> If all items are cancelled, the order will be automatically cancelled
      </NotificationBar>
      
      <FormRow label="Cancel Reason">
        <FormInputTextArea ref="textEntryArea" v-model="cancelReason" placeholder="Enter reason and details for item cancellation" field="cancelReason" />
      </FormRow>
      <FormHorizontalRule />
      <FormRow label="Charge Stock/Production Costs" helpTip="If set, will charge order items based on any stock or production already completed">
        <FormSwitch v-model="chargeStockProduction" :booleanMode="true" field="chargeStockProduction" />
      </FormRow>
      <FormHorizontalRule />
      <FormRow label="Additional Cancellation Fee" helpTip="Fee in addition to the current stock and processing fees">
        <FormSwitch v-model="chargeAdditionalFee" :booleanMode="true" />
      </FormRow>
      <FormRow label="" helpTip="" :fieldCount="2" v-show="chargeAdditionalFee">
        <template v-slot:field1>
          <FormInputStockCode v-model="cancellationStockInfo.stockId" :stockType="InventoryStockTypes.general" />
        </template>
        <template v-slot:field2>
          <FormInputCurrency v-model="cancelFee" field="cancelFee" :note="cancelFeeNote" v-if="cancellationStockInfo.stockId && !cancellationStockInfo.loading" />
          <Spinner v-if="cancellationStockInfo.loading" />
        </template>
      </FormRow>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import Utils from '@/utils/utils'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import { PriceTypes, UOMTypes, InventoryStockTypes, AccountSettingKeys } from '@/store/types'

export default {
  name: "CancelOrderItemDialog",
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    FormInputStockCode
  },
  props: {
    order: Object,
    orderId: Number,
    orderItemId: Number
  },
  data() {
    return {
      responseInfo: {},
      dialogForm: {},
      submitting: false,
      chargeStockProduction: false,
      cancelReason: '',
      chargeAdditionalFee: false,
      cancelFee: 0,
      accountData: {},
      cancellationStockInfo: {
        stockId: null,
        price: 0,
        minChargePrice: 0,
        uomType: '',
        loading: false,
      }
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.textEntryArea.focus()
    })
  },
  computed: {
    orderEntryDuration() {
      return Math.min(this.order.orderEntryDuration || 0, 1)
    },
    cancelFeeNote() {
      if(this.cancellationStockInfo.uomType == UOMTypes.time)
        return `Based on order entry duration of ${this.orderEntryDuration} minute(s) at ` + this.$options.filters.formatCurrency(this.cancellationStockInfo.price) + `/min with minimum charge of ` + this.$options.filters.formatCurrency(this.cancellationStockInfo.minChargePrice)

      return `Based on fixed cancellation fee of ` + this.$options.filters.formatCurrency(this.cancellationStockInfo.price)
    }    
  },
  watch: {
    'cancellationStockInfo.stockId': function() {
      // Query the customer price for the stock id
      this.lookupCancellationFeePrice()
    }
  },
  methods: {
    show() {
      // Reset form
      this.responseInfo = { }
      this.dialogForm = { }

      // Get default cancellation fee stock
      this.$apiService.adminService.getAccountSettingForKey(AccountSettingKeys.orderEntryCancellationFeeStockId, 0)
      .then(settingValue => {
        if(this.cancellationStockInfo.stockId == null)
          this.cancellationStockInfo.stockId = parseInt(settingValue || null)
      })
      .catch(err => { })

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      let cancelFeeDisplay = Utils.formatCurrency(this.cancelFee)

      var additionalFee = 0
      if(this.chargeAdditionalFee)
        additionalFee = this.cancelFee

      var message = 'Confirm the cancellation of the order item?'
      message += `<br /><br />All stock/production already processed <strong>${this.chargeStockProduction ? 'will' : 'will not'}</strong> be charged`
      if(additionalFee > 0)
        message += `<br /><br />An additional fee of <strong>${cancelFeeDisplay}</strong> will be charged`

      this.confirmMessage('Confirm Item Cancellation?', '', { html: message })
      .then(success => {
        if(success) {
          this._submitCancelItem()
        }
      })      
    },
    _submitCancelItem() {
      this.submitting = true

      let updateData = {
        cancelReason: this.cancelReason,
        cancelFee: this.chargeAdditionalFee ? this.cancelFee : 0,
        cancelStockId: this.cancellationStockInfo.stockId,
        chargeStockProduction: this.chargeStockProduction
      }

      this.$apiService.ordersService.cancelOrderItem(this.orderId, this.orderItemId, updateData)
      .then(response => {
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.$data.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.submitting = false
      })
    },
    lookupCancellationFeePrice() {
      this.cancelFee = 0
      if(!this.cancellationStockInfo.stockId)
        return

      let stockLookupFilter = {
        priceType: PriceTypes.customer,
        contactId: this.order.contactId,
        stockId: this.cancellationStockInfo.stockId
      }

      // Lookup the default delivery fee
      this.cancellationStockInfo.loading = true
      this.$apiService.ordersService.getContactStockLookup(stockLookupFilter)
      .then(response => {
        this.cancellationStockInfo.price = parseFloat(response.data.price.value)
        this.cancellationStockInfo.minChargePrice = parseFloat(response.data.price.minChargePrice)
        this.cancellationStockInfo.uomType = response.data.stock.uomType

        // Set default fee
        if(this.cancellationStockInfo.uomType == UOMTypes.time) {
          this.cancelFee = this.cancellationStockInfo.price * this.orderEntryDuration
        } else {
          this.cancelFee = this.cancellationStockInfo.price
        }
        if(this.cancellationStockInfo.minChargePrice > 0)
          this.cancelFee = Math.max(this.cancelFee, this.cancellationStockInfo.minChargePrice)
      })
      .finally(() => {
        this.cancellationStockInfo.loading = false
      })
    }
  }
}
</script>